// ─────────────────────────────────────────────────────────────────
// Glass-i — India GST constants (Stage 6A.2 — frontend mirror).
//
// Public mirror of `backend/src/utils/indiaGst.js`. Used by Customer
// Master forms to populate the State dropdown, auto-fill PAN / state
// code from GSTIN, and offer the registration-type / SEZ-type pickers.
//
// IMPORTANT: keep this list synchronized with the backend list. The
// backend is authoritative; mismatches are a code smell — fix here,
// don't add ad-hoc exceptions.
// ─────────────────────────────────────────────────────────────────

export const GST_STATE_CODES = Object.freeze([
  { code: '01', name: 'Jammu & Kashmir' },
  { code: '02', name: 'Himachal Pradesh' },
  { code: '03', name: 'Punjab' },
  { code: '04', name: 'Chandigarh' },
  { code: '05', name: 'Uttarakhand' },
  { code: '06', name: 'Haryana' },
  { code: '07', name: 'Delhi' },
  { code: '08', name: 'Rajasthan' },
  { code: '09', name: 'Uttar Pradesh' },
  { code: '10', name: 'Bihar' },
  { code: '11', name: 'Sikkim' },
  { code: '12', name: 'Arunachal Pradesh' },
  { code: '13', name: 'Nagaland' },
  { code: '14', name: 'Manipur' },
  { code: '15', name: 'Mizoram' },
  { code: '16', name: 'Tripura' },
  { code: '17', name: 'Meghalaya' },
  { code: '18', name: 'Assam' },
  { code: '19', name: 'West Bengal' },
  { code: '20', name: 'Jharkhand' },
  { code: '21', name: 'Odisha' },
  { code: '22', name: 'Chhattisgarh' },
  { code: '23', name: 'Madhya Pradesh' },
  { code: '24', name: 'Gujarat' },
  { code: '25', name: 'Daman and Diu' },
  { code: '26', name: 'Dadra and Nagar Haveli and Daman and Diu' },
  { code: '27', name: 'Maharashtra' },
  { code: '29', name: 'Karnataka' },
  { code: '30', name: 'Goa' },
  { code: '31', name: 'Lakshadweep' },
  { code: '32', name: 'Kerala' },
  { code: '33', name: 'Tamil Nadu' },
  { code: '34', name: 'Puducherry' },
  { code: '35', name: 'Andaman and Nicobar Islands' },
  { code: '36', name: 'Telangana' },
  { code: '37', name: 'Andhra Pradesh' },
  { code: '38', name: 'Ladakh' },
  { code: '97', name: 'Other Territory' },
  { code: '99', name: 'Centre Jurisdiction' }
]);

const STATE_CODE_TO_NAME = new Map(GST_STATE_CODES.map((s) => [s.code, s.name]));

export const GST_REGISTRATION_TYPES = Object.freeze([
  { value: 'registered_regular',     label: 'Registered — Regular' },
  { value: 'registered_composition', label: 'Registered — Composition' },
  { value: 'unregistered',           label: 'Unregistered' },
  { value: 'consumer',               label: 'Consumer (B2C)' },
  { value: 'sez',                    label: 'SEZ' },
  { value: 'export',                 label: 'Export' },
  { value: 'deemed_export',          label: 'Deemed Export' }
]);

export const REGISTRATION_TYPES_REQUIRING_GSTIN = Object.freeze([
  'registered_regular',
  'registered_composition',
  'sez'
]);

export const GST_VERIFICATION_STATUSES = Object.freeze([
  { value: 'not_verified',    label: 'Not verified' },
  { value: 'verified',        label: 'Verified' },
  { value: 'failed',          label: 'Verification failed' },
  { value: 'manual_override', label: 'Manual override' }
]);

export const SEZ_TYPES = Object.freeze([
  { value: 'sez_unit',      label: 'SEZ Unit' },
  { value: 'sez_developer', label: 'SEZ Developer' }
]);

export const CUSTOMER_STATUSES = Object.freeze([
  { value: 'active',   label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'blocked',  label: 'Blocked' }
]);

// Curated category seed list — masters can free-type any value, this
// just gives the dropdown sensible defaults for the glass industry.
export const CUSTOMER_CATEGORY_SUGGESTIONS = Object.freeze([
  'Builder',
  'Architect',
  'Glazier',
  'Fabricator',
  'Retailer',
  'Distributor',
  'OEM',
  'Walk-in',
  'Showroom'
]);

// Matches the backend GSTIN_REGEX exactly so frontend + backend agree
// before a save attempt. Tolerates lowercase input — caller uppercases
// the value first (see gstinInput in utils/validators.js).
export const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z][A-Z][0-9A-Z]$/;
export const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

export function isValidGstin(v) {
  return typeof v === 'string' && GSTIN_REGEX.test(v.toUpperCase());
}

export function isValidPan(v) {
  return typeof v === 'string' && PAN_REGEX.test(v.toUpperCase());
}

export function stateNameFromCode(code) {
  return STATE_CODE_TO_NAME.get(code) || null;
}

export function panFromGstin(gstin) {
  if (!isValidGstin(gstin)) return null;
  return String(gstin).toUpperCase().slice(2, 12);
}

export function stateCodeFromGstin(gstin) {
  if (typeof gstin !== 'string') return null;
  const head = gstin.slice(0, 2);
  return STATE_CODE_TO_NAME.has(head) ? head : null;
}

export function registrationTypeRequiresGstin(type) {
  return REGISTRATION_TYPES_REQUIRING_GSTIN.includes(type);
}

// Option list ready for AntD `<Select options={...} />`.
export const STATE_SELECT_OPTIONS = GST_STATE_CODES.map((s) => ({
  value: s.code,
  label: `${s.name} (${s.code})`
}));
